'use client';

import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import PageHeader from '@/components/ui/PageHeader';
import JsonLd from '@/components/ui/JsonLd';
import { generateBreadcrumbSchema, generateLocalBusinessSchema } from '@/lib/schema';
import { SITE_URL } from '@/lib/site';
import { LazyMapEmbed } from '@/components/ui/LazyMapEmbed';
import { sendGoogleAdsConversion } from '@/lib/google-ads-conversion';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    subject: 'Genel Bilgi',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Breadcrumb öğeleri
  const breadcrumbItems = [
    { name: 'Ana Sayfa', url: SITE_URL },
    { name: 'İletişim', url: `${SITE_URL}/iletisim` },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Optimize edilmiş API fonksiyonunu kullanıyoruz
      const apiModule = await import('@/lib/api');
      const result = await apiModule.submitContactForm(formData);
      
      if (result.success) {
        setIsSubmitting(false);
        setSubmitSuccess(true);
        sendGoogleAdsConversion();
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          subject: 'Genel Bilgi',
        });
      } else {
        setIsSubmitting(false);
        setSubmitError(result.message || 'Bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
      }
    } catch (error) {
      console.error('Form gönderme hatası:', error);
      setIsSubmitting(false);
      setSubmitError('Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.');
    }
  };

  return (
    <div className="py-8 md:py-12">
      {/* JSON-LD Şemaları */}
      <JsonLd schema={generateLocalBusinessSchema()} />
      <JsonLd schema={generateBreadcrumbSchema(breadcrumbItems)} />
      
      <div className="container mx-auto px-4">
        <PageHeader 
          title="İletişim" 
          subtitle="İstoç, Topkapı ve tüm Türkiye'ye imalatçıdan doğrudan satış. Sorularınız ve siparişleriniz için bizimle iletişime geçin."
          theme="dark"
          size="medium"
          breadcrumbs={[
            { label: 'İletişim', url: '/iletisim' }
          ]}
        />

        <div className="flex flex-col lg:flex-row gap-8 mt-8 mb-12">
          {/* İletişim Formu */}
          <div className="w-full lg:w-2/3">
            <div className="bg-white rounded-lg shadow-sm p-6 md:p-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Bize Mesaj Gönderin</h2>
              
              {submitSuccess ? (
                <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-md mb-6">
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Mesajınız başarıyla gönderildi. En kısa sürede size dönüş yapacağız.</span>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {submitError && (
                    <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-md">
                      <div className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{submitError}</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                        Adınız Soyadınız*
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        E-posta Adresiniz*
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Telefon Numaranız
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                        Konu
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="Genel Bilgi">Genel Bilgi</option>
                        <option value="Fiyat Teklifi">Fiyat Teklifi</option>
                        <option value="Proje Danışmanlığı">Proje Danışmanlığı</option>
                        <option value="Satış Sonrası Destek">Satış Sonrası Destek</option>
                        <option value="Diğer">Diğer</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Mesajınız*
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    ></textarea>
                  </div>
                  
                  <div className="flex justify-end">
                    <Button 
                      type="submit" 
                      disabled={isSubmitting}
                      className={`min-w-[150px] ${isSubmitting ? 'opacity-70 cursor-wait' : ''}`}
                    >
                      {isSubmitting ? 'Gönderiliyor...' : 'Gönder'}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
          
          {/* İletişim Bilgileri */}
          <div className="w-full lg:w-1/3">
            <div className="bg-white rounded-lg shadow-sm p-6 md:p-8 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">İletişim Bilgilerimiz</h3>
              
              <div className="space-y-4">
                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-semibold text-gray-900">Telefon & WhatsApp</h4>
                    <p className="text-sm text-gray-600">0532 345 72 27</p>
                    <p className="text-sm text-gray-600">0536 552 40 45</p>
                    <p className="text-sm text-gray-600">(0212) 256 73 08</p>
                    <p className="text-sm text-gray-600">(0212) 256 73 09 (Faks)</p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-semibold text-gray-900">Merkez</h4>
                    <p className="text-sm text-gray-600">
                      Piyalepaşa Mh. Baruthane Sk. No:23
                      <br />
                      BEYOĞLU İSTANBUL
                    </p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-semibold text-gray-900">Showroom</h4>
                    <p className="text-sm text-gray-600">
                      Maltepe Mh. Davutpaşa Cd. Tim 2 İş Merkezi No:584
                      <br />
                      ZEYTİNBURNU İSTANBUL
                    </p>
                  </div>
                </div>

                <div className="flex">
                  <div className="flex-shrink-0 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-semibold text-gray-900">Firma Unvanı</h4>
                    <p className="text-sm text-gray-600">
                      FİLİZ DEKORASYON KOZMETİK CAM MOBİLYA VE KUAFÖR MALZEMELERİ SANAYİ TİCARET LİMİDET ŞİRKETİ
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Sık Sorulan Sorular */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Sık Sorulan Sorular</h2>
          
          <div className="space-y-4">
            {/* Ürünler ve Hizmetler */}
            <div className="border border-gray-200 rounded-lg">
              <button 
                className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg focus:outline-none"
                onClick={() => document.getElementById('urunler')?.classList.toggle('hidden')}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">Ürünler ve Hizmetler</span>
                  <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              <div id="urunler" className="hidden px-6 py-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Hangi ürün gruplarınız mevcut?</h4>
                    <p className="mt-2 text-gray-600">Kuaför malzemeleri, mobilya, cam ürünleri ve kozmetik ürünleri başta olmak üzere geniş bir ürün yelpazemiz bulunmaktadır.</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Özel tasarım mobilya yaptırabiliyor muyuz?</h4>
                    <p className="mt-2 text-gray-600">Evet, müşterilerimizin isteklerine göre özel tasarım mobilya hizmeti sunuyoruz.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sipariş ve Teslimat */}
            <div className="border border-gray-200 rounded-lg">
              <button 
                className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg focus:outline-none"
                onClick={() => document.getElementById('siparis')?.classList.toggle('hidden')}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">Sipariş ve Teslimat</span>
                  <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              <div id="siparis" className="hidden px-6 py-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Teslimat süreniz nedir?</h4>
                    <p className="mt-2 text-gray-600">Stokta olan ürünler için 1-3 iş günü, özel üretim ürünler için 15-30 iş günü arasında teslimat yapıyoruz.</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Türkiye'nin her yerine gönderim yapıyor musunuz?</h4>
                    <p className="mt-2 text-gray-600">Evet, Türkiye'nin tüm illerine gönderim yapıyoruz. Ayrıca yurtdışı gönderilerimiz de mevcuttur.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Ödeme ve Fiyatlandırma */}
            <div className="border border-gray-200 rounded-lg">
              <button 
                className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg focus:outline-none"
                onClick={() => document.getElementById('odeme')?.classList.toggle('hidden')}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">Ödeme ve Fiyatlandırma</span>
                  <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              <div id="odeme" className="hidden px-6 py-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Hangi ödeme yöntemlerini kabul ediyorsunuz?</h4>
                    <p className="mt-2 text-gray-600">Nakit, kredi kartı, havale/EFT ve çek ile ödeme kabul ediyoruz. Kredi kartına taksit imkanımız mevcuttur.</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">Toplu alımlarda indirim yapıyor musunuz?</h4>
                    <p className="mt-2 text-gray-600">Evet, toplu alımlarda özel fiyatlandırma uyguluyoruz. Detaylı bilgi için bizimle iletişime geçebilirsiniz.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Garanti ve İade */}
            <div className="border border-gray-200 rounded-lg">
              <button 
                className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg focus:outline-none"
                onClick={() => document.getElementById('garanti')?.classList.toggle('hidden')}
              >
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-900">Garanti ve İade</span>
                  <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>
              <div id="garanti" className="hidden px-6 py-4">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900">Ürünleriniz garantili mi?</h4>
                    <p className="mt-2 text-gray-600">Tüm ürünlerimiz 2 yıl garantilidir. Üretim hatası olan ürünlerde değişim yapılmaktadır.</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">İade ve değişim politikanız nedir?</h4>
                    <p className="mt-2 text-gray-600">Ürünlerimizde 14 gün içerisinde iade hakkı sunuyoruz. Özel üretim ürünlerde iade ve değişim yapılmamaktadır.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Harita */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-2 mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h2 className="text-2xl font-bold text-gray-900">Mağazamız</h2>
          </div>
          <div className="h-[400px] overflow-hidden rounded-lg bg-gray-100 shadow-inner">
            <LazyMapEmbed
              title="Filiz Kozmetik mağaza konumu — Google Haritalar"
              fill
            />
          </div>
        </div>
      </div>
    </div>
  );
} 